import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { EntityPageLayout } from '@/components/common';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from '@/components/ui/drawer';
import { useIsMobile } from '@/hooks/use-mobile';
import { PermissionGate } from '@/components/auth/PermissionGate';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

// Feature components
import { CreateVenueForm, VenueTable, VenueDetails } from '../components';
import { useVenues, useCreateVenueFromPlace } from '../hooks/useVenueManagement';
import { type CreateVenueFromPlaceInput } from '../schemas/venueSchemas';
import type { Venue } from '../types';

export function VenuesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const isMobile = useIsMobile();

  // React Query hooks
  const { data, isLoading, error } = useVenues();
  const createVenueMutation = useCreateVenueFromPlace();

  const venues = data?.venues || [];
  const totalCount = data?.total_count || 0;

  // Handle /new route
  useEffect(() => {
    if (location.pathname === '/venues/new') {
      if (hasPermission('venue_management:create')) {
        setIsCreateDrawerOpen(true);
      } else {
        // Redirect to main page if no permission
        navigate('/venues');
      }
    }
  }, [location.pathname, hasPermission, navigate]);

  // Handle drawer close - navigate back to main page
  const handleCreateDrawerChange = (open: boolean) => {
    setIsCreateDrawerOpen(open);
    if (!open && location.pathname === '/venues/new') {
      navigate('/venues');
    }
  };

  // Handle create venue
  const handleCreateVenue = async (values: CreateVenueFromPlaceInput) => {
    try {
      await createVenueMutation.mutateAsync(values);
      handleCreateDrawerChange(false);
    } catch {
      // Error is handled by the mutation hook
    }
  };

  return (
    <>
      <Helmet>
        <title>Venues - Urban Finch Admin</title>
      </Helmet>
      <EntityPageLayout
        title="Venues"
        description="Search and manage venues from Google Places"
        permission="venue_management:read"
        testIdPrefix="venues"
        isLoading={isLoading}
        error={
          error instanceof Error
            ? error
            : error
              ? new Error(String(error))
              : undefined
        }
        headerActions={
          <PermissionGate permission="venue_management:create">
            <Button
              onClick={() => handleCreateDrawerChange(true)}
              data-testid="create-venue-button"
            >
              Create Venue
            </Button>
          </PermissionGate>
        }
      >
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-1">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Venues
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className="text-2xl font-bold"
                  data-testid="venue-total-count"
                >
                  {totalCount}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Venues Table */}
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>All Venues</CardTitle>
                  <CardDescription>
                    Manage venues from Google Places
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <VenueTable
                data={venues}
                isLoading={isLoading}
                error={error instanceof Error ? error : null}
                onViewDetails={setSelectedVenue}
              />
            </CardContent>
          </Card>
        </div>
      </EntityPageLayout>

      {/* Create Venue Drawer */}
      <Drawer
        open={isCreateDrawerOpen}
        onOpenChange={handleCreateDrawerChange}
        direction={isMobile ? 'bottom' : 'right'}
      >
        <DrawerContent className={isMobile ? 'h-[90vh]' : 'w-[500px] h-full'}>
          <DrawerHeader>
            <DrawerTitle>Create New Venue</DrawerTitle>
            <DrawerDescription>
              Search for a place and add it as a venue
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4 flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-400">
            <CreateVenueForm
              onSubmit={handleCreateVenue}
              formId="create-venue-form"
            />
          </div>
          <DrawerFooter>
            <Button
              type="submit"
              form="create-venue-form"
              disabled={createVenueMutation.isPending}
              data-testid="venue-form-submit"
            >
              {createVenueMutation.isPending ? 'Creating...' : 'Create Venue'}
            </Button>
            <Button
              variant="outline"
              onClick={() => handleCreateDrawerChange(false)}
              disabled={createVenueMutation.isPending}
              data-testid="venue-form-cancel"
            >
              Cancel
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Venue Details Drawer */}
      <Drawer
        open={!!selectedVenue}
        onOpenChange={(open) => !open && setSelectedVenue(null)}
        direction={isMobile ? 'bottom' : 'right'}
      >
        <DrawerContent className={isMobile ? 'h-[90vh]' : 'w-[500px] h-full'}>
          <DrawerHeader>
            <DrawerTitle>Venue Details</DrawerTitle>
            <DrawerDescription>
              View venue information and operating hours
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4 flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-400">
            {selectedVenue && <VenueDetails venue={selectedVenue} />}
          </div>
          <DrawerFooter>
            <Button
              variant="outline"
              onClick={() => setSelectedVenue(null)}
            >
              Close
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}